import { getAnalytics } from "@tm/insurance/analytics";
import { trackComponentView } from "@tm/insurance/analytics/xdm";
import {
  FlexComp,
  H2Tag,
  H4Tag,
  H5Tag,
  ImageComponent,
  ParaTag,
} from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import { theme } from "@tm/utility/global";
import { useCallback, useRef } from "react";
import { InnerContentWrap } from "../../CommonStyle/CommonStyles.style";
import FormattedText, { TextNodeType } from "../FormattedText/FormattedText";
import { BenefitItem, WhatsCoveredStyle } from "./WhatsCovered.style";

interface WhatsCoveredProps {
  data?: {
    title: TextNodeType;
    info: TextNodeType;
    cardImg: string;
  }[];
  title?: TextNodeType;
  info?: TextNodeType;
}

const WhatsCovered = ({ data, title, info }: WhatsCoveredProps) => {
  const device = useBreakpointV2();
  const analytics = getAnalytics();
  const hasTracked = useRef<boolean>(false);

  // Track component view when it comes into viewport
  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    trackComponentView({
      name: "section_3_shown",
      type: "card",
    });
    if (!hasTracked.current) {
      analytics?.trackLandingEvents({
        actionType: "whats_covered_seen",
      });
    }
    hasTracked.current = true;
  }, []);

  const { ref: intersectionRef } = useIntersectionObserver(handleIntersection, {
    threshold: 0.5, // Trigger when 50% of component is visible
    triggerOnLeaveViewport: true,
  });

  return (
    <WhatsCoveredStyle ref={intersectionRef}>
      <InnerContentWrap>
        {device == "lg" ? (
          <H2Tag>{title && <FormattedText value={title} />}</H2Tag>
        ) : (
          <H5Tag>{title && <FormattedText value={title} />}</H5Tag>
        )}
        <ParaTag
          fontSize={device == "lg" ? "large" : "sm"}
          color={theme.greyText}
          margin="0 0 66px"
        >
          {info && <FormattedText value={info} />}
        </ParaTag>
        <FlexComp
          gap={device == "lg" ? (data?.length === 2 ? "50px" : "82px") : "24px"}
        >
          {data &&
            data.map((item, index) => {
              return (
                <BenefitItem key={index}>
                  {item.cardImg && (
                    <ImageComponent
                      width={device == "lg" ? "164px" : "80px"}
                      height={device == "lg" ? "164px" : "80px"}
                      src={item.cardImg}
                    />
                  )}
                  {device == "lg" ? (
                    <H4Tag margin="0 0 8px">
                      {item.title && <FormattedText value={item.title} />}
                    </H4Tag>
                  ) : (
                    <ParaTag
                      fontSize="large"
                      fontWeight="black"
                      margin="0 0 8px"
                    >
                      {" "}
                      {item.title && <FormattedText value={item.title} />}
                    </ParaTag>
                  )}
                  <ParaTag
                    fontSize={device == "lg" ? "large" : "sm"}
                    color={device == "lg" ? theme.greyText7 : theme.blackText}
                  >
                    {item.info && <FormattedText value={item.info} />}
                  </ParaTag>
                </BenefitItem>
              );
            })}
        </FlexComp>
      </InnerContentWrap>
    </WhatsCoveredStyle>
  );
};

export default WhatsCovered;
